import { Checkbox, InputLabel, Stack, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useContext, useMemo } from 'react';
import WarningPopup from '../../../Popups/WarningPopup';
import { useTripRateContext } from '../../TripRateContext';
import { SessionContext } from '../../../Contexts/SessionContext';
import { CounterBadge } from '../../../Utilities/CounterBadge';

export default function ParkingSpacesPerDwellingRange() {
    const { surveys } = useTripRateContext();
    const { sessionState, setSessionField } = useContext(SessionContext);

    const roundToTwoDecimals = (value: number) => Number(value.toFixed(2));

    const minMaxParam = useMemo(() => {
        if (!Array.isArray(surveys) || surveys.length === 0) return null;

        const validValues = surveys
            .map(s => s.parkingSpacesPerDwelling)
            .filter(v => typeof v === 'number' && !isNaN(v)) as number[];

        if (validValues.length === 0) return null;

        const min = Math.min(...validValues);
        const max = Math.max(...validValues);
        const avg = validValues.reduce((a, b) => a + b, 0) / validValues.length;

        setSessionField('primaryFilters.minParkingSpacesPerDwelling', roundToTwoDecimals(min));
        setSessionField('primaryFilters.maxParkingSpacesPerDwelling', roundToTwoDecimals(max));

        return { min: roundToTwoDecimals(min), max: roundToTwoDecimals(max), avg };
    }, [surveys]);

    const parkingPerDwellingChecked = sessionState?.primaryFilters?.parkingSpacesPerDwellingChecked ?? true;
    const min = sessionState?.primaryFilters?.minParkingSpacesPerDwelling ?? '';
    const max = sessionState?.primaryFilters?.maxParkingSpacesPerDwelling ?? '';

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSessionField(
            'primaryFilters.parkingSpacesPerDwellingChecked',
            e.target.checked
        );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const parsed = value === '' ? '' : Number(value);

        if (name === 'minParkingSpacesPerDwelling') {
            setSessionField('primaryFilters.minParkingSpacesPerDwelling', parsed);
        } else if (name === 'maxParkingSpacesPerDwelling') {
            setSessionField('primaryFilters.maxParkingSpacesPerDwelling', parsed);
        }
    };

    return (
        <Grid container component={Paper} size={12} alignItems="center" sx={{ position: 'relative' }}>
            <CounterBadge value={'5'} />

            <Grid size={12}>
                <Typography textAlign='center'>Parking Spaces Per Dwelling Range<WarningPopup
                    title="Warning on Parking Spaces Per Dwelling"
                    content="By default, all surveys are included. Unticking this box will restrict results to sites with known parking space per dwelling values only."
                    type="warning"
                /></Typography>

            </Grid>

            <Grid size={6}>
                <Stack spacing={1} direction="row" alignItems="center">
                    <InputLabel id="parkingSpacesPerDwellingLabel">
                        All Sites Selected
                    </InputLabel>
                    <Checkbox
                        name="parkingPerDwellingChecked"
                        checked={parkingPerDwellingChecked}
                        onChange={handleCheckboxChange}
                    />
                </Stack>
            </Grid>

            <Grid container size={12}>
                <Grid size={4}>
                    <Stack spacing={3}>
                        <Typography variant="body1">
                            Minimum: {minMaxParam ? minMaxParam.min.toFixed(2) : '-'}
                        </Typography>
                        <Typography variant="body1">
                            Maximum: {minMaxParam ? minMaxParam.max.toFixed(2) : '-'}
                        </Typography>
                    </Stack>
                </Grid>

                {!parkingPerDwellingChecked && (
                    <Grid size={8}>
                        <Stack spacing={1}>
                            <Grid size={12}>
                                <TextField
                                    name="minParkingSpacesPerDwelling"
                                    value={min}
                                    onChange={handleInputChange}
                                    label="From:"
                                    type="number"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    name="maxParkingSpacesPerDwelling"
                                    value={max}
                                    onChange={handleInputChange}
                                    label="To:"
                                    type="number"
                                />
                            </Grid>
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
}
